import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { addDriverCert, deleteDriverCert, getDriverAssignedBlocks, getDriverRoute } from 'actions/profile'
import { deleteVehicle, patchDecodePlate, resolveConflictingVIN, setActiveVehicle } from 'actions/vehicles'
import { DriverRoute, DrivingProfile, ProfileVehicle } from 'types/profile'
import { ModalOptions, initialModalOptions } from 'types/global'
import { getActiveVehicleId, hasLicensePlateLookupFailure, VehicleFormStates } from 'util/vehicle'
import { useInsurancePollingContext } from 'context/InsurancePollingContext'

type EditableField = 'plate' | 'state'

type UseLicensePlateOnlyInputViewModelProps = {
  licensePlate?: string | null
  licensePlateState?: string | null
  vehicleId: number
  disabled?: boolean
  missingVIN: boolean
  setFormState: React.Dispatch<React.SetStateAction<VehicleFormStates | null>>
  setLicensePlate: React.Dispatch<React.SetStateAction<any>>
  setLicensePlateState?: React.Dispatch<React.SetStateAction<string | null>>
  setModalOptions?: React.Dispatch<React.SetStateAction<ModalOptions>>
}

export const useLicensePlateOnlyInputViewModel = (props: UseLicensePlateOnlyInputViewModelProps) => {
  const profile = useSelector<any, DrivingProfile>((state) => state.profile)

  const [licensePlateInputValue, setLicensePlateInputValue] = useState(props.licensePlate || '')
  const [originalPlate, setOriginalPlate] = useState(props.licensePlate || '')
  const [originalState, setOriginalState] = useState<string | null>(props.licensePlateState || null)
  const [isEditingPlate, setIsEditingPlate] = useState(false)
  const [isEditingState, setIsEditingState] = useState(false)

  const dispatch = useDispatch()
  const { disabled, setLicensePlateState } = props

  useEffect(() => {
    if (disabled) {
      setLicensePlateInputValue(originalPlate)
      setLicensePlateState?.(originalState)
      setIsEditingPlate(false)
      setIsEditingState(false)
    }
  }, [disabled, originalPlate, originalState, setLicensePlateState])

  useEffect(() => {
    if (isEditingPlate || isEditingState) return

    const nextPlate = props.licensePlate || ''
    const nextState = props.licensePlateState || null
    setLicensePlateInputValue(nextPlate)
    setOriginalPlate(nextPlate)
    setOriginalState(nextState)
  }, [props.licensePlate, props.licensePlateState, isEditingPlate, isEditingState])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (props.disabled) {
      return
    }
    const value = e.target.value
    // Prevent any non-alphanumeric characters from being pasted
    const alphanumericValue = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase()
    setLicensePlateInputValue(alphanumericValue)
  }

  const handleLicensePlateStateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (props.disabled) {
      return
    }
    const value = e.target.value
    props.setLicensePlateState?.(value)
  }

  const beginEdit = (field: EditableField) => {
    if (props.disabled) return
    switch (field) {
      case 'plate':
        setIsEditingPlate(true)
        break
      case 'state':
        setIsEditingState(true)
        break
      default: {
        const _exhaustiveCheck: never = field
        return _exhaustiveCheck
      }
    }
  }

  const cancelEdit = (field: EditableField) => {
    switch (field) {
      case 'plate':
        setLicensePlateInputValue(originalPlate)
        setIsEditingPlate(false)
        break
      case 'state':
        props.setLicensePlateState?.(originalState)
        setIsEditingState(false)
        break
      default: {
        const _exhaustiveCheck: never = field
        return _exhaustiveCheck
      }
    }
  }

  const plateChanged = licensePlateInputValue !== originalPlate
  const stateChanged = (props.licensePlateState || '') !== (originalState || '')
  const hasChanges = plateChanged || stateChanged

  const handleSubmit = useCallback(async () => {
    if (!licensePlateInputValue || !props.licensePlateState) return
    const hadPreviousPlate = Boolean(originalPlate?.trim())
    props.setFormState('searchingVehicle')
    props.setLicensePlate(licensePlateInputValue)
    const lookupResponse = await patchDecodePlate(
      profile.id,
      props.vehicleId,
      licensePlateInputValue,
      props.licensePlateState
    )(dispatch)

    if (!lookupResponse.success) {
      if (lookupResponse.status == 422) {
        props.setFormState('unsupportedType')
        return
      } else if (lookupResponse.status == 500) {
        props.setFormState('serverError')
        return
      } else {
        switch (lookupResponse.error) {
          case 'Not Found':
            props.setFormState('vehicleNotFound')
            break
          case 'Conflict':
            setIsEditingPlate(false)
            setIsEditingState(false)
            props.setFormState(null)
            props.setModalOptions?.({ modalOpen: true, modalType: 'conflicting-vin-found' })
            break
          default:
            props.setFormState('vehicleNotFound')
            break
        }
        return
      }
    }

    setIsEditingPlate(false)
    setIsEditingState(false)
    setOriginalPlate(licensePlateInputValue)
    setOriginalState(props.licensePlateState ?? null)
    dispatch({ type: 'CLEAR_ALL_FLASH' })
    dispatch({
      type: 'PUSH_FLASH',
      flash: {
        messageKey: hadPreviousPlate ? 'dweb_flash_license_plate_updated' : 'dweb_flash_license_plate_added',
        type: 'success',
      },
    })
    props.setFormState(null)
  }, [licensePlateInputValue, originalPlate, props, dispatch, profile.id])

  const isEditing = isEditingPlate || isEditingState

  const displayErrorLabel = props.licensePlate && !props.licensePlateState

  return {
    licensePlateInputValue,
    isEditing,
    isEditingPlate,
    isEditingState,
    hasChanges,
    displayErrorLabel,
    handleInputChange,
    handleLicensePlateStateChange,
    beginEdit,
    cancelEdit,
    handleSubmit,
  }
}

export const useVehicleInfoViewModel = (
  vehicle: ProfileVehicle,
  active: boolean,
  setModalOptions?: React.Dispatch<React.SetStateAction<ModalOptions>>
) => {
  const { status, vehicleId } = useInsurancePollingContext()
  const profile = useSelector<any, DrivingProfile>((state) => state.profile)
  const isWebView = useSelector<any, boolean>((state) => state.config.isWebview)
  const isLocked = useSelector<any, boolean>((state) => state.isLocked)
  const driverRoute = useSelector<any, DriverRoute | null>((state) => state.driverRoute)
  const driverHasActiveBlock = useSelector<any, boolean>((state) => state.driverHasActiveBlock)
  const driverActiveBlockStatus = useSelector<any, 'loading' | 'loaded'>((state) => state.driverActiveBlockStatus)
  const dispatch = useDispatch()
  const history = useHistory()

  const [isActive, setIsActive] = useState<boolean>(active)
  const [licensePlateState, setLicensePlateState] = useState<string | null>(vehicle.details.license_plate_state || '')
  const [formState, setFormState] = useState<VehicleFormStates | null>(null)
  const [licensePlate, setLicensePlate] = useState<any>(null)

  // if a driver is on a route, they cannot change their active vehicle
  const onRoute = Array.isArray(driverRoute?.stops) && driverRoute?.stops.length > 0

  // surface alerts if driver does not have certain info on file
  const licensePlateLookupFailure = hasLicensePlateLookupFailure(vehicle)
  const missingLicensePlate = vehicle.details.license_plate == null
  const missingVIN = vehicle.details.vin == null

  const errorLabel =
    vehicle.details.license_plate && !vehicle.details.license_plate_state && !licensePlateState
      ? 'Missing license plate state'
      : undefined

  const vinConflicts = Array.from(
    new Map(
      vehicle.vin_conflicts?.map((c) => [c.type_id, { displayName: c.display_name, typeId: c.type_id, vin: c.vin }])
    ).values()
  ).slice(0, 5)

  useEffect(() => {
    if (licensePlateLookupFailure && missingVIN && status !== 'polling') {
      dispatch({
        type: 'PUSH_FLASH',
        flash: {
          id: 'no-vehicle-match-license-plate-error',
          messageKey: 'dweb_flash_no_vehicle_match_retry_license_plate',
          type: 'error',
        },
      })
    } else if (!missingVIN) {
      // Clear the banner if VIN data comes in later
      dispatch({
        type: 'CLEAR_FLASH',
        id: 'no-vehicle-match-license-plate-error',
      })
    }
  }, [licensePlateLookupFailure, missingVIN, status, dispatch])

  useEffect(() => {
    // Show Upload in Progress modal only on the vehicle having insurance uploaded and processed
    if (status == 'polling' && vehicleId == vehicle.details.id) {
      setModalOptions?.({ modalOpen: true, modalType: 'insurance-upload-in-progress' })
    } else if (status === 'accepted' || status === 'rejected') {
      setModalOptions?.({ modalOpen: false, modalType: null })
    }
  }, [status, vehicleId, vehicle.details.id, setModalOptions])

  useEffect(() => {
    if (vehicle.vin_conflicts && vehicle.vin_conflicts.length > 0) {
      setModalOptions?.({ modalOpen: true, modalType: 'conflicting-vin-found' })
    }
  }, [vehicle.vin_conflicts, setModalOptions])

  const handleActiveVehicleToggle = useCallback(async () => {
    try {
      if (!isActive) {
        setIsActive(!isActive)
        setActiveVehicle(profile.id, vehicle.details.id)(dispatch)

        {
          /* TODO: temporarily added this cert request as part of WEB-2441 vehicle data collection */
        }
        await Promise.all([
          vehicle.specs.category == 'pickup_truck' || vehicle.specs.model == 'Pickup'
            ? deleteDriverCert('enclosed_vehicle')(dispatch)
            : addDriverCert('enclosed_vehicle')(dispatch),
        ])
      }
    } catch (_) {
      dispatch({
        type: 'PUSH_FLASH',
        flash: {
          messageKey: 'dweb_flash_vehicle_set_active_problem',
          type: 'error',
        },
      })
      throw new Error()
    }
  }, [dispatch, isActive, profile.id, vehicle.details.id, vehicle.specs.category, vehicle.specs.model])

  const handleDelete = useCallback(async () => {
    try {
      setModalOptions?.({ modalOpen: false, modalType: null })
      await deleteVehicle(vehicle.details.id, profile.id)(dispatch)
      history.push('/account/driving-info')
      dispatch({ type: 'PUSH_FLASH', flash: { messageKey: 'dweb_flash_vehicle_deleted', type: 'success' } })
    } catch (_) {
      dispatch({
        type: 'PUSH_FLASH',
        flash: {
          messageKey: 'dweb_flash_vehicle_delete_problem',
          type: 'error',
        },
      })
      throw new Error()
    }
  }, [dispatch, history, profile.id, setModalOptions, vehicle.details.id])

  const handleConflictingVIN = useCallback(
    async (data: any) => {
      const conflicts = vinConflicts ?? []
      const selectedIndex = Number(data['conflicting-vin-select'])
      const selectedConflict = conflicts[selectedIndex]

      if (!selectedConflict) {
        dispatch({
          type: 'PUSH_FLASH',
          flash: { messageKey: 'dweb_flash_select_vehicle', type: 'error' },
        })
        return
      }
      const res = await resolveConflictingVIN(selectedConflict.vin, vehicle.details.id)(dispatch)

      if (res.success) {
        dispatch({
          type: 'PUSH_FLASH',
          flash: { messageKey: 'dweb_flash_vehicle_updated', type: 'success' },
        })
      } else {
        dispatch({
          type: 'PUSH_FLASH',
          flash: {
            messageKey: 'dweb_flash_vehicle_update_problem',
            type: 'error',
          },
        })
      }
    },
    [dispatch, vehicle.details.id, vinConflicts]
  )

  return {
    isWebView,
    isLocked,
    isActive,
    licensePlateState,
    setLicensePlateState,
    formState,
    setFormState,
    licensePlate,
    setLicensePlate,
    onRoute,
    driverHasActiveBlock,
    driverActiveBlockStatus,
    licensePlateLookupFailure,
    missingLicensePlate,
    missingVIN,
    errorLabel,
    vinConflicts,
    handleActiveVehicleToggle,
    handleDelete,
    handleConflictingVIN,
  }
}

export const useVehicleInfoPageViewModel = (vehicleId: number) => {
  const isWebView = useSelector<any, boolean>((state) => state.config.isWebview)
  const vehicles = useSelector<any, ProfileVehicle[]>((state) => state.vehicles)
  const dispatch = useDispatch()

  const [activeVehicleId, setActiveVehicleId] = useState<number>(Number(getActiveVehicleId(vehicles)))
  const [modalOptions, setModalOptions] = useState(initialModalOptions)

  const vehicle = vehicles.find((vehicle) => vehicle.details.id === vehicleId)

  useEffect(() => {
    setActiveVehicleId(Number(getActiveVehicleId(vehicles)))
  }, [vehicles, dispatch])

  useEffect(() => {
    getDriverRoute()(dispatch)
    getDriverAssignedBlocks()(dispatch)
  }, [dispatch])

  useEffect(() => {
    if (vehicle?.plate_lookup?.result === 'pending') {
      setModalOptions({ modalOpen: true, modalType: 'pending-plate-lookup' })
    }
  }, [vehicle?.plate_lookup?.result])

  return {
    isWebView,
    vehicles,
    activeVehicleId,
    modalOptions,
    setModalOptions,
    vehicle,
  }
}
